import React, { useState } from 'react';
import styled from 'styled-components';

const CaloriesContainer = styled.div`
  padding: 20px;
  background-color: #1E1E1E;
  color: white;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  position: relative;
  z-index: 1000;
`;

const SearchContainer = styled.div`
  margin-bottom: 20px;
  width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px;
  background-color: #181616;
  border: 1px solid #d1eaff;
  border-radius: 4px;
  color: white;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #777;
  }
`;

const ResultsContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
  width: 100%;
`;

const FoodItem = styled.div`
  padding: 10px;
  margin: 5px 0;
  background-color: #333;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #444;
  }
`;

const NutritionInfo = styled.div`
  margin-top: 20px;
  padding: 20px;
  background-color: #333;
  border-radius: 8px;
  width: 100%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const FoodTitle = styled.h3`
  font-size: 16px;
  margin: 0 0 20px 0;
  color: #fff;
  font-weight: 500;
`;

const NutrientRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const NutrientLabel = styled.span`
  color: rgba(255, 255, 255, 0.7);
`;

const NutrientValue = styled.span`
  color: #fff;
  font-weight: 500;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 10px;
  color: #888;
`;

const FoodCaloriesCalculator = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedFood, setSelectedFood] = useState(null);
  const [loading, setLoading] = useState(false);

  const API_KEY = 'PYCOlyhxlLEavJtg4xOgw1DciCgFvfQmds6EYZR7'; // Replace with your API key
  const API_ENDPOINT = 'https://api.nal.usda.gov/fdc/v1';

  const searchFood = async () => {
    if (!searchTerm) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${API_ENDPOINT}/foods/search?api_key=${API_KEY}&query=${searchTerm}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      const data = await response.json();
      setSearchResults(data.foods || []);
    } catch (error) {
      console.error('Error searching for food:', error);
    }
    setLoading(false);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    if (e.target.value.length >= 3) {
      searchFood();
    }
  };

  const selectFood = async (food) => {
    setSelectedFood(food);
    setSearchResults([]);
    setSearchTerm('');
  };

  return (
    <CaloriesContainer>
      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="Search for a food..."
          value={searchTerm}
          onChange={handleSearch}
        />
      </SearchContainer>

      {loading && <LoadingText>Loading...</LoadingText>}

      <ResultsContainer>
        {searchResults.map((food) => (
          <FoodItem key={food.fdcId} onClick={() => selectFood(food)}>
            <div>{food.description}</div>
          </FoodItem>
        ))}
      </ResultsContainer>

      {selectedFood && (
        <NutritionInfo>
          <FoodTitle>{selectedFood.description}</FoodTitle>
          <NutrientRow>
            <NutrientLabel>Calories</NutrientLabel>
            <NutrientValue>
              {selectedFood.foodNutrients.find(n => n.nutrientName === 'Energy')?.value || 'N/A'} kcal
            </NutrientValue>
          </NutrientRow>
          <NutrientRow>
            <NutrientLabel>Protein</NutrientLabel>
            <NutrientValue>
              {selectedFood.foodNutrients.find(n => n.nutrientName === 'Protein')?.value || 'N/A'} g
            </NutrientValue>
          </NutrientRow>
          <NutrientRow>
            <NutrientLabel>Fat</NutrientLabel>
            <NutrientValue>
              {selectedFood.foodNutrients.find(n => n.nutrientName === 'Total lipid (fat)')?.value || 'N/A'} g
            </NutrientValue>
          </NutrientRow>
          <NutrientRow>
            <NutrientLabel>Carbohydrates</NutrientLabel>
            <NutrientValue>
              {selectedFood.foodNutrients.find(n => n.nutrientName === 'Carbohydrate, by difference')?.value || 'N/A'} g
            </NutrientValue>
          </NutrientRow>
          <NutrientRow>
            <NutrientLabel>Fiber</NutrientLabel>
            <NutrientValue>
              {selectedFood.foodNutrients.find(n => n.nutrientName === 'Fiber, total dietary')?.value || 'N/A'} g
            </NutrientValue>
          </NutrientRow>
          <NutrientRow>
            <NutrientLabel>Sugar</NutrientLabel>
            <NutrientValue>
              {selectedFood.foodNutrients.find(n => n.nutrientName === 'Sugars, total including NLEA')?.value || 'N/A'} g
            </NutrientValue>
          </NutrientRow>
          <NutrientRow>
            <NutrientLabel>Sodium</NutrientLabel>
            <NutrientValue>
              {selectedFood.foodNutrients.find(n => n.nutrientName === 'Sodium, Na')?.value || 'N/A'} mg
            </NutrientValue>
          </NutrientRow>
          <NutrientRow>
            <NutrientLabel>Cholesterol</NutrientLabel>
            <NutrientValue>
              {selectedFood.foodNutrients.find(n => n.nutrientName === 'Cholesterol')?.value || 'N/A'} mg
            </NutrientValue>
          </NutrientRow>
          <NutrientRow>
            <NutrientLabel>Vitamin C</NutrientLabel>
            <NutrientValue>
              {selectedFood.foodNutrients.find(n => n.nutrientName === 'Vitamin C, total ascorbic acid')?.value || 'N/A'} mg
            </NutrientValue>
          </NutrientRow>
          <NutrientRow>
            <NutrientLabel>Iron</NutrientLabel>
            <NutrientValue>
              {selectedFood.foodNutrients.find(n => n.nutrientName === 'Iron, Fe')?.value || 'N/A'} mg
            </NutrientValue>
          </NutrientRow>
        </NutritionInfo>
      )}
    </CaloriesContainer>
  );
};

export default FoodCaloriesCalculator; 